import React, { useState, useEffect } from 'react';
import { 
  Megaphone, Plus, Edit, Trash2, Play, Pause, Loader2,
  Calendar, Tag, Target, Percent, Clock, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import api from '@/services/api';

const CampaignsPanel = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    campaign_type: 'discount_code',
    promo_code: '',
    discount_type: 'percentage',
    discount_value: 10,
    target_type: 'all',
    target_areas: [],
    start_date: '',
    end_date: '',
    promotional_message: ''
  });

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get('/api/admin/campaigns');
      if (response.data.success) {
        setCampaigns(response.data.campaigns);
      }
    } catch (error) {

      toast.error('Failed to load campaigns');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingCampaign(null);
    setFormData({
      name: '',
      description: '',
      campaign_type: 'discount_code',
      promo_code: '',
      discount_type: 'percentage',
      discount_value: 10,
      target_type: 'all',
      target_areas: [],
      start_date: '',
      end_date: '',
      promotional_message: ''
    });
    setShowModal(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setFormData({
      name: campaign.name,
      description: campaign.description || '',
      campaign_type: campaign.campaign_type,
      promo_code: campaign.promo_code || '',
      discount_type: campaign.discount_rule?.type || 'percentage',
      discount_value: campaign.discount_rule?.value || 10,
      target_type: campaign.target?.target_type || 'all',
      target_areas: campaign.target?.areas || [],
      start_date: campaign.start_date?.split('T')[0] || '',
      end_date: campaign.end_date?.split('T')[0] || '',
      promotional_message: campaign.promotional_message || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error('Campaign name is required');
      return;
    }

    setIsSaving(true);
    try {
      if (editingCampaign) {
        await api.put(`/api/admin/campaigns/${editingCampaign.campaign_id}`, formData);
        toast.success('Campaign updated');
      } else {
        await api.post('/api/admin/campaigns', formData);
        toast.success('Campaign created');
      }
      setShowModal(false);
      fetchCampaigns();
    } catch (error) {

      toast.error('Failed to save campaign');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggle = async (campaign) => {
    try {
      const response = await api.post(`/api/admin/campaigns/${campaign.campaign_id}/toggle`);
      if (response.data.success) {
        toast.success(`Campaign ${response.data.status === 'active' ? 'activated' : 'paused'}`);
        fetchCampaigns();
      }
    } catch (error) {

      toast.error('Failed to toggle campaign');
    }
  };

  const handleDelete = async (campaign) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await api.delete(`/api/admin/campaigns/${campaign.campaign_id}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {

      toast.error('Failed to delete campaign');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': 'bg-gray-100 text-gray-800',
      'active': 'bg-green-100 text-green-800',
      'paused': 'bg-yellow-100 text-yellow-800',
      'ended': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getCampaignTypeIcon = (type) => {
    switch (type) {
      case 'discount_code': return <Tag className="h-4 w-4" />;
      case 'time_limited': return <Clock className="h-4 w-4" />;
      case 'targeted': return <Target className="h-4 w-4" />;
      default: return <Megaphone className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-[#039fff]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#001A47]">Campaigns</h2>
          <p className="text-gray-500">Create and manage promotional campaigns</p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          New Campaign
        </Button>
      </div>

      {/* Campaign Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#001A47]">{campaigns.length}</p>
              <p className="text-sm text-gray-500">Total Campaigns</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-green-600">
                {campaigns.filter(c => c.status === 'active').length}
              </p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-yellow-600">
                {campaigns.filter(c => c.status === 'paused').length}
              </p>
              <p className="text-sm text-gray-500">Paused</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-blue-600">
                {campaigns.reduce((sum, c) => sum + (c.uses || 0), 0)}
              </p>
              <p className="text-sm text-gray-500">Total Uses</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Campaigns List */}
      {campaigns.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No campaigns yet. Create your first campaign!</p>
            <Button onClick={handleCreate} className="mt-4">
              <Plus className="h-4 w-4 mr-2" />
              Create Campaign
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {campaigns.map((campaign) => (
            <Card key={campaign.campaign_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    {getCampaignTypeIcon(campaign.campaign_type)}
                    <CardTitle className="text-lg">{campaign.name}</CardTitle>
                  </div>
                  <Badge className={getStatusColor(campaign.status)}>
                    {campaign.status}
                  </Badge>
                </div>
                <CardDescription className="line-clamp-2">
                  {campaign.description}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {campaign.promo_code && (
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-gray-400" />
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">
                      {campaign.promo_code}
                    </code>
                  </div>
                )}
                
                {campaign.discount_rule && (
                  <div className="flex items-center gap-2 text-sm">
                    <Percent className="h-4 w-4 text-green-600" />
                    <span>
                      {campaign.discount_rule.value}
                      {campaign.discount_rule.type === 'percentage' ? '% off' : ' off'}
                    </span>
                  </div>
                )}

                {campaign.start_date && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Calendar className="h-4 w-4" />
                    <span>
                      {new Date(campaign.start_date).toLocaleDateString()} - 
                      {campaign.end_date ? new Date(campaign.end_date).toLocaleDateString() : 'Ongoing'}
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Uses: {campaign.uses || 0}</span>
                </div>

                <div className="flex gap-2 pt-2 border-t">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleToggle(campaign)}
                  >
                    {campaign.status === 'active' ? (
                      <>
                        <Pause className="h-4 w-4 mr-1" />
                        Pause
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-1" />
                        Activate
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleEdit(campaign)}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(campaign)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Campaign Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g., Summer Sale 2025"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(f => ({ ...f, description: e.target.value }))}
                placeholder="Campaign description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Campaign Type</Label>
                <Select 
                  value={formData.campaign_type}
                  onValueChange={(v) => setFormData(f => ({ ...f, campaign_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="discount_code">Discount Code</SelectItem>
                    <SelectItem value="time_limited">Time Limited Offer</SelectItem>
                    <SelectItem value="targeted">Targeted Campaign</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Target Audience</Label>
                <Select 
                  value={formData.target_type}
                  onValueChange={(v) => setFormData(f => ({ ...f, target_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Customers</SelectItem>
                    <SelectItem value="students">Students Only</SelectItem>
                    <SelectItem value="business">Business Accounts</SelectItem>
                    <SelectItem value="returning">Returning Customers</SelectItem>
                    <SelectItem value="area_specific">Area Specific</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Promo Code</Label>
                <Input
                  value={formData.promo_code}
                  onChange={(e) => setFormData(f => ({ ...f, promo_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., SUMMER25"
                />
              </div>

              <div className="space-y-2">
                <Label>Discount Type</Label>
                <Select 
                  value={formData.discount_type}
                  onValueChange={(v) => setFormData(f => ({ ...f, discount_type: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage</SelectItem>
                    <SelectItem value="fixed_amount">Fixed Amount (R)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Discount Value</Label>
              <Input
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData(f => ({ ...f, discount_value: parseFloat(e.target.value) }))}
                placeholder={formData.discount_type === 'percentage' ? '10' : '50'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(f => ({ ...f, start_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData(f => ({ ...f, end_date: e.target.value }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Promotional Message</Label>
              <Textarea
                value={formData.promotional_message}
                onChange={(e) => setFormData(f => ({ ...f, promotional_message: e.target.value }))}
                placeholder="Message to display to customers..."
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              {editingCampaign ? 'Update' : 'Create'} Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CampaignsPanel;
